import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  advertiserApi,
  type Campaign,
  type CampaignStatus,
  type SponsoredPlacement,
} from '../../services/advertiserApi';
import { CampaignFormDialog } from './CampaignFormDialog';

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function statusColor(status: CampaignStatus): 'default' | 'success' | 'warning' | 'info' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  if (status === 'ended' || status === 'archived') return 'default';
  return 'info';
}

/**
 * Campaign detail / management page. Shows the campaign summary,
 * an Edit button (re-uses CampaignFormDialog), and the placements
 * table with add + remove controls.
 */
export function CampaignDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [addingPlacement, setAddingPlacement] = useState(false);
  const [newProductListingId, setNewProductListingId] = useState('');
  const [newBidDollars, setNewBidDollars] = useState('');
  const [placementError, setPlacementError] = useState<string | null>(null);

  const campaignQuery = useQuery({
    queryKey: ['advertiser', 'campaign', id],
    queryFn: () => advertiserApi.getCampaign(id),
    enabled: Boolean(id),
  });

  const placementsQuery = useQuery({
    queryKey: ['advertiser', 'placements', id],
    queryFn: () => advertiserApi.listPlacements(id),
    enabled: Boolean(id),
  });

  const addMutation = useMutation({
    mutationFn: () =>
      advertiserApi.addPlacement(id, {
        productListingId: newProductListingId.trim(),
        bidCents: Math.round(Number(newBidDollars) * 100),
      }),
    onSuccess: () => {
      setNewProductListingId('');
      setNewBidDollars('');
      setAddingPlacement(false);
      setPlacementError(null);
      return queryClient.invalidateQueries({ queryKey: ['advertiser', 'placements', id] });
    },
    onError: (err: Error) => {
      setPlacementError(err.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (placementId: string) => advertiserApi.removePlacement(id, placementId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advertiser', 'placements', id] }),
  });

  if (campaignQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading campaign" />
      </Box>
    );
  }
  if (campaignQuery.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load campaign: {(campaignQuery.error as Error).message}
      </Alert>
    );
  }

  const campaign: Campaign = campaignQuery.data!.campaign;
  const placements: SponsoredPlacement[] = placementsQuery.data?.placements ?? [];

  return (
    <Box>
      <Button component={RouterLink} to="/dashboard/advertiser/campaigns" sx={{ mb: 2 }}>
        ← Back to campaigns
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem' }}>
            {campaign.name}
          </Typography>
          <Chip
            size="small"
            label={campaign.status}
            color={statusColor(campaign.status)}
            sx={{ mt: 1 }}
          />
        </Box>
        <Button variant="outlined" onClick={() => setEditOpen(true)}>
          Edit campaign
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryRow label="Daily budget" value={dollars(campaign.dailyBudgetCents)} />
        <SummaryRow label="Total budget" value={dollars(campaign.totalBudgetCents)} />
        <SummaryRow label="Spent" value={dollars(campaign.spentCents)} />
        <SummaryRow label="Starts" value={campaign.startsAt?.slice(0, 10) ?? '—'} />
        <SummaryRow label="Ends" value={campaign.endsAt?.slice(0, 10) ?? '—'} />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem' }}>
          Sponsored placements
        </Typography>
        <Button variant="contained" onClick={() => setAddingPlacement(true)}>
          Add placement
        </Button>
      </Stack>

      {addingPlacement ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Product listing ID"
              value={newProductListingId}
              onChange={(e) => setNewProductListingId(e.target.value)}
              fullWidth
              helperText="UUID of the product listing to sponsor."
            />
            <TextField
              label="Bid (USD)"
              type="number"
              value={newBidDollars}
              onChange={(e) => setNewBidDollars(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ minWidth: 160 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                disabled={
                  !newProductListingId.trim() ||
                  !Number.isFinite(Number(newBidDollars)) ||
                  addMutation.isPending
                }
                onClick={() => addMutation.mutate()}
              >
                Add
              </Button>
              <Button
                onClick={() => {
                  setAddingPlacement(false);
                  setNewProductListingId('');
                  setNewBidDollars('');
                  setPlacementError(null);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
          {placementError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {placementError}
            </Alert>
          ) : null}
        </Paper>
      ) : null}

      {placementsQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading placements" />
        </Box>
      ) : placements.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">No sponsored placements yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a placement to sponsor a product listing.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Sponsored placements">
            <TableHead>
              <TableRow>
                <TableCell>Product listing</TableCell>
                <TableCell>Bid</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {placements.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {p.productListingId}
                  </TableCell>
                  <TableCell>{dollars(p.bidCents)}</TableCell>
                  <TableCell>{p.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={`Remove placement ${p.id}`}
                      onClick={() => removeMutation.mutate(p.id)}
                      size="small"
                      color="error"
                      disabled={removeMutation.isPending}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CampaignFormDialog
        open={editOpen}
        campaign={campaign}
        onClose={() => setEditOpen(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['advertiser', 'campaign', id] })}
      />
    </Box>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
}

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function DeleteIcon() {
  return (
    <Box
      component="svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      aria-hidden="true"
      sx={{ fill: 'currentColor' }}
    >
      <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zm3-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h5v2H4V5h5V4z" />
    </Box>
  );
}

export default CampaignDetailPage;
